'use client';
import React, { useEffect, useRef } from 'react';
import { useEditor, Patch } from './EditorContext';
import { sanitizePastedHtml } from '@/components/blog-editor/BlockRenderer';

type Tag = keyof JSX.IntrinsicElements;

type Props = {
  /** Stable id used as the dirty-map key. Required. */
  id: string;
  /** HTML tag to render (default: 'span'). */
  as?: Tag;
  /** Initial text from data. */
  value: string;
  /** Patch to register when content changes. */
  buildPatch: (newValue: string) => Patch;
  /** className passes through. */
  className?: string;
  style?: React.CSSProperties;
  /** Allow line breaks (Enter inserts \n instead of blurring). Default: false. */
  multiline?: boolean;
  /** Placeholder shown only in edit mode when value is empty. */
  placeholder?: string;
  /** When true, `value` may contain inline HTML (bold/italic/link/sup/sub —
   * e.g. a post title formatted via the main editor's FormatToolbar) and is
   * rendered/edited as rich HTML instead of plain text. Off by default so
   * existing plain-text fields (dek, etc.) are unaffected — a literal "<" or
   * "&" typed into those should never be misread as markup. */
  richText?: boolean;
};

/**
 * A click-to-edit text element. In view mode it's a normal element with the
 * given tag and value. In edit mode it becomes contentEditable with a subtle
 * dashed outline on hover and a blue outline on focus — Medium-style.
 */
export function EditableText({
  id, as, value, buildPatch, className, style, multiline = false, placeholder, richText = false,
}: Props) {
  const { editing, setPatch, clearPatch } = useEditor();
  const ref = useRef<HTMLElement>(null);

  // Reset content when value changes (e.g., after save reload)
  useEffect(() => {
    if (!ref.current) return;
    if (richText) {
      if (ref.current.innerHTML !== value) ref.current.innerHTML = value;
    } else {
      if (ref.current.textContent !== value) ref.current.textContent = value;
    }
  }, [value, editing, richText]);

  const Tag = (as || 'span') as any;

  const handleInput = () => {
    const next = richText ? (ref.current?.innerHTML ?? '') : (ref.current?.textContent ?? '');
    if (next === value) {
      clearPatch(id);
    } else {
      setPatch(id, buildPatch(next));
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!multiline && e.key === 'Enter') {
      e.preventDefault();
      (e.target as HTMLElement).blur();
    }
  };

  const handlePaste = richText ? (e: React.ClipboardEvent) => {
    e.preventDefault();
    const html = e.clipboardData.getData('text/html');
    const inserted = html
      ? sanitizePastedHtml(html, multiline)
      : e.clipboardData.getData('text/plain').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/\r?\n/g, multiline ? '<br>' : ' ');
    document.execCommand('insertHTML', false, inserted);
    handleInput();
  } : undefined;

  // richText + not editing: render the raw HTML (e.g. a formatted title)
  // directly rather than through the contentEditable/textContent path below.
  if (richText && !editing) {
    return (
      <Tag
        className={className}
        style={style}
        dangerouslySetInnerHTML={{ __html: value }}
      />
    );
  }

  return (
    <Tag
      ref={ref}
      className={['onpage-editable', editing && 'onpage-editable--on', className].filter(Boolean).join(' ')}
      style={style}
      contentEditable={editing}
      suppressContentEditableWarning
      spellCheck={editing}
      data-placeholder={placeholder}
      onInput={editing ? handleInput : undefined}
      onPaste={editing ? handlePaste : undefined}
      onKeyDown={editing ? handleKeyDown : undefined}
    >
      {richText ? undefined : value}
    </Tag>
  );
}
